import React from 'react';
import cn from 'clsx';
import { useStoreon } from 'storeon/react';
import { RELATED_SERVICES_STORE_NAME, selectActualMeasure } from 'modules/related-services';
import {
  Accordion,
  ButtonWithGAClickButton,
  LinkWithGAClickButton,
  Panel,
  Section,
  SectionSubTitle,
  Typography,
} from 'common/components';
import { BATHROOM_COLLECTIONS_ELEMENT_BLOCK } from 'landings/bathroom-collections/bathroom-collections-constants';
import { analyticsUtils } from 'utils/analytics-utils';
import {
  CHOOSE_COLLECTION,
  MEASURE_ROOM,
  ORDER_INSTALLATION,
  PLACE_ORDER,
} from './bathroom-collections-steps-section-constants';
import {
  bathroomCollectionsStepButton,
  bathroomCollectionsStepDescription,
  bathroomCollectionsStepLink,
  bathroomCollectionsStepsAccordion,
  bathroomCollectionsStepTitle,
  bathroomCollectionsStepTitleFirst,
  bathroomCollectionsStepTitleFourth,
  bathroomCollectionsStepTitleSecond,
  bathroomCollectionsStepTitleThird,
} from './bathroom-collections-steps-section-styles';

export const BathroomCollectionsStepsSection: React.FC = () => {
  const { relatedServices } = useStoreon(RELATED_SERVICES_STORE_NAME);
  const actualMeasure = selectActualMeasure(relatedServices);

  /**
   * Обработчик нажатия на колапс панели
   */
  const handlePanelClick = (question: string) => (_: string, isOpen: boolean) => {
    if (!isOpen) {
      analyticsUtils.clickButtonEvent(
        BATHROOM_COLLECTIONS_ELEMENT_BLOCK,
        question.slice(0, 100),
        'Как обновить мебель в ванной комнате'
      );
    }
  };

  return (
    <Section>
      <SectionSubTitle margin="m">Как обновить мебель в&nbsp;ванной комнате</SectionSubTitle>

      <Accordion className={bathroomCollectionsStepsAccordion}>
        <Panel
          className={cn(bathroomCollectionsStepTitle, bathroomCollectionsStepTitleFirst)}
          isQuestion={false}
          name="chooseCollection"
          onClick={handlePanelClick(CHOOSE_COLLECTION)}
          title={CHOOSE_COLLECTION}>
          <div className={bathroomCollectionsStepDescription}>
            <Typography itemProp="text" variant="s" size="body_long">
              Определитесь с&nbsp;внешним видом вашей будущей ванной комнаты и&nbsp;выберите подходящую
              коллекцию
            </Typography>
          </div>
        </Panel>
        <Panel
          className={cn(bathroomCollectionsStepTitle, bathroomCollectionsStepTitleSecond)}
          isQuestion={false}
          name="measureRoom"
          onClick={handlePanelClick(MEASURE_ROOM)}
          title={MEASURE_ROOM}>
          <div className={bathroomCollectionsStepDescription}>
            <Typography itemProp="text" variant="s" size="body_long">
              Если вы не знаете размеры ванной комнаты, лучше заказать профессиональный замер или измерить
              самостоятельно по нашей{' '}
              <LinkWithGAClickButton
                className={bathroomCollectionsStepLink}
                elementBlock={`${BATHROOM_COLLECTIONS_ELEMENT_BLOCK} / steps`}
                elementText="инструкции"
                elementName={MEASURE_ROOM}
                title="Как измерить ванную комнату"
                href="/advice/santehnika/kak-izmerit-vannuyu-komnatu/">
                инструкции
              </LinkWithGAClickButton>{' '}
              для выбора сантехники и мебели
            </Typography>
            {actualMeasure && (
              <ButtonWithGAClickButton
                adaptive={false}
                size="m"
                className={bathroomCollectionsStepButton}
                elementBlock={`${BATHROOM_COLLECTIONS_ELEMENT_BLOCK} / steps`}
                elementName={MEASURE_ROOM}
                elementText="Заказать замер"
                href={actualMeasure.serviceURL}
                target="_blank">
                Заказать замер
              </ButtonWithGAClickButton>
            )}
          </div>
        </Panel>
        <Panel
          className={cn(bathroomCollectionsStepTitle, bathroomCollectionsStepTitleThird)}
          isQuestion={false}
          name="placeOrder"
          onClick={handlePanelClick(PLACE_ORDER)}
          title={PLACE_ORDER}>
          <div className={bathroomCollectionsStepDescription}>
            <Typography itemProp="text" variant="s" size="body_long">
              Оплатите покупку, и&nbsp;мы&nbsp;доставим мебель для ванной комнаты в&nbsp;удобное для вас
              время
            </Typography>
          </div>
        </Panel>
        <Panel
          className={cn(bathroomCollectionsStepTitle, bathroomCollectionsStepTitleFourth)}
          isQuestion={false}
          name="orderInstallation"
          onClick={handlePanelClick(ORDER_INSTALLATION)}
          title={ORDER_INSTALLATION}>
          <div className={bathroomCollectionsStepDescription}>
            <Typography itemProp="text" variant="s" size="body_long">
              Не&nbsp;просто продаём товары, а&nbsp;помогаем с&nbsp;ремонтом и&nbsp;обустройством квартиры
              или дома
            </Typography>
            {actualMeasure && (
              <ButtonWithGAClickButton
                adaptive={false}
                size="m"
                className={bathroomCollectionsStepButton}
                elementBlock={`${BATHROOM_COLLECTIONS_ELEMENT_BLOCK} / steps`}
                elementName={ORDER_INSTALLATION}
                elementText="Заказать установку"
                href="/uslugi/catalogue/ustanovka-santehniki/"
                target="_blank">
                Заказать установку
              </ButtonWithGAClickButton>
            )}
          </div>
        </Panel>
      </Accordion>
    </Section>
  );
};
